/**
 * useEcgridPackageTest Hook
 * Orchestrates data fetching via mediator and store updates
 */

import { useCallback, useEffect } from 'react';
import { logger } from '@/application/core';
import { GetSampleDataQuery, mediator, type SampleDataResponse } from '@/application';
import { useEcgridPackageTestStore } from './EcgridPackageTestPage.store';

/**
 * Hook return type
 */
interface UseEcgridPackageTestResult {
  data: SampleDataResponse | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch and manage sample data
 * Uses mediator.send() for CQRS pattern
 */
export function useEcgridPackageTest(): UseEcgridPackageTestResult {
  const data = useEcgridPackageTestStore((s) => s.data);
  const loading = useEcgridPackageTestStore((s) => s.loading);
  const error = useEcgridPackageTestStore((s) => s.error);
  const setData = useEcgridPackageTestStore((s) => s.setData);
  const setLoading = useEcgridPackageTestStore((s) => s.setLoading);
  const setError = useEcgridPackageTestStore((s) => s.setError);

  const fetchData = useCallback(async () => {
    setLoading(true);

    try {
      // MediatR-style: send query to mediator
      const result = await mediator.send<SampleDataResponse>(new GetSampleDataQuery());
      setData(result);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch sample data');
      setError(error);
      logger.error('useEcgridPackageTest', 'Error:', err);
    }
  }, [setData, setLoading, setError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    refetch: fetchData,
  };
}
